/**
 * Add Service Dialog Component
 * @description 성능 최적화된 새 서비스 추가 다이얼로그
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui/primitives';
import { getApiEndpoint } from '@shared/config';
import { ServiceIconPicker } from './ServiceIconPicker';

interface ServiceConfig {
  folder: string;
  name: string;
  description: string;
  labels: string[];
  icon: string;
  ports: {
    frontend: number;
    backend: number;
    'frontend-admin'?: number;
  };
}

interface AddServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (service: ServiceConfig) => Promise<void>;
  availableIndexes: number[];
}

// 포트 계산 함수
const calculatePortsFromIndex = (index: number) => ({
  frontend: 4000 + index,
  backend: 5000 + index,
  'frontend-admin': 6000 + index,
});

// 폴더명 정규화 함수 (메모이제이션)
const normalizeFolderName = (value: string): string => {
  let normalized = value.toLowerCase();
  normalized = normalized.replace(/[^a-z0-9-]/g, '');
  normalized = normalized.replace(/-+/g, '-');
  if (normalized.startsWith('-')) normalized = normalized.slice(1);
  if (normalized.endsWith('-')) normalized = normalized.slice(0, -1);
  return normalized;
};

const AddServiceDialogComponent: React.FC<AddServiceDialogProps> = ({
  open,
  onOpenChange,
  onSave,
  availableIndexes,
}) => {
  // 로컬 상태 관리 - 부모 컴포넌트 리렌더링 방지
  const [folder, setFolder] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('Shield');
  const [labelsInput, setLabelsInput] = useState('');
  const [components, setComponents] = useState({
    backend: false,
    frontend: false,
    'frontend-admin': false,
  });
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // 다이얼로그가 열릴 때 초기화 (availableIndexes는 open 시점의 값만 사용)
  useEffect(() => {
    if (open) {
      setFolder('');
      setName('');
      setDescription('');
      setIcon('Shield');
      setLabelsInput('');
      setComponents({ backend: false, frontend: false, 'frontend-admin': false });
      setSelectedIndex(null);
      setIsLoading(false);
      
      // 첫 번째 사용 가능한 index 자동 선택
      if (availableIndexes.length > 0) {
        const firstIndex = availableIndexes[0];
        setSelectedIndex(firstIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // availableIndexes는 의존성에서 제거 (open 시점의 값만 사용)

  // 포트 계산 메모이제이션
  const ports = useMemo(() => {
    if (!selectedIndex) return { frontend: 0, backend: 0, 'frontend-admin': 0 };
    return calculatePortsFromIndex(selectedIndex);
  }, [selectedIndex]);

  // 폴더명 입력 핸들러 - 필터링 비활성화
  const handleFolderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // 필터링 없이 원본 입력값 그대로 사용
    setFolder(inputValue);
  }, []);

  // 이름 입력 핸들러
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  // 설명 입력 핸들러
  const handleDescriptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
  }, []);

  // 아이콘 선택 핸들러
  const handleIconSelect = useCallback((iconName: string) => {
    setIcon(iconName);
  }, []);

  // 컴포넌트 선택 핸들러
  const handleComponentChange = useCallback((component: 'backend' | 'frontend' | 'frontend-admin', checked: boolean) => {
    setComponents(prev => ({ ...prev, [component]: checked }));
  }, []);

  // Index 선택 핸들러
  const handleIndexChange = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  // 포트 입력 핸들러
  const handlePortChange = useCallback((portType: 'frontend' | 'backend' | 'frontend-admin', value: number) => {
    // 포트는 index 기반으로만 설정되므로 여기서는 처리하지 않음
  }, []);

  // 저장 핸들러
  const handleSave = useCallback(async () => {
    if (!folder.trim()) {
      alert('폴더명을 입력해주세요.');
      return;
    }
    if (!name.trim()) {
      alert('서비스명을 입력해주세요.');
      return;
    }
    if (!description.trim()) {
      alert('서비스 설명을 입력해주세요.');
      return;
    }
    if (!components.backend && !components.frontend && !components['frontend-admin']) {
      alert('최소 하나의 컴포넌트를 선택해주세요.');
      return;
    }
    if (!selectedIndex) {
      alert('Port Index를 선택해주세요.');
      return;
    }

    // 포트 유효성 검사
    if (components.frontend && (ports.frontend < 4001 || ports.frontend > 4100)) {
      alert('Frontend 포트는 4001-4100 범위 내여야 합니다.');
      return;
    }
    if (components.backend && (ports.backend < 5001 || ports.backend > 5100)) {
      alert('Backend 포트는 5001-5100 범위 내여야 합니다.');
      return;
    }
    if (components['frontend-admin'] && (ports['frontend-admin'] < 6001 || ports['frontend-admin'] > 6100)) {
      alert('Frontend Admin 포트는 6001-6100 범위 내여야 합니다.');
      return;
    }

    setIsLoading(true);
    try {
      const labels = labelsInput
        .split(',')
        .map(l => l.trim())
        .filter(l => l.length > 0);

      const serviceData: ServiceConfig = {
        folder: folder.trim(),
        name: name.trim(),
        description: description.trim(),
        labels,
        icon,
        ports: {
          frontend: components.frontend ? ports.frontend : 0,
          backend: components.backend ? ports.backend : 0,
          ...(components['frontend-admin'] ? { 'frontend-admin': ports['frontend-admin'] } : {}),
        },
      };

      // 백엔드 API가 요구하는 형식으로 변환 (components 포함)
      const apiPayload = {
        folder: serviceData.folder,
        name: serviceData.name,
        description: serviceData.description,
        icon: serviceData.icon,
        components: {
          backend: components.backend,
          frontend: components.frontend,
          'frontend-admin': components['frontend-admin'],
        },
        ports: {
          ...(components.backend && ports.backend ? { backend: ports.backend } : {}),
          ...(components.frontend && ports.frontend ? { frontend: ports.frontend } : {}),
          ...(components['frontend-admin'] && ports['frontend-admin'] ? { 'frontend-admin': ports['frontend-admin'] } : {}),
        },
      };

      await onSave(apiPayload as any);
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving service:', error);
    } finally {
      setIsLoading(false);
    }
  }, [folder, name, description, icon, labelsInput, components, selectedIndex, ports, onSave, onOpenChange]);

  // 취소 핸들러
  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // SelectContent 아이템 메모이제이션
  const selectItems = useMemo(() => {
    return availableIndexes.map((index) => {
      const indexPorts = calculatePortsFromIndex(index);
      return (
        <SelectItem key={index} value={index.toString()} className="pr-8">
          <span className="font-medium">Index {index}</span>
          <span className="ml-2 text-muted-foreground">
            FE {indexPorts.frontend} · BE {indexPorts.backend} · Admin {indexPorts['frontend-admin']}
          </span>
        </SelectItem>
      );
    });
  }, [availableIndexes]);

  // 다이얼로그가 닫혀있을 때는 렌더링하지 않음
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden gap-4">
        <DialogHeader className="shrink-0">
          <DialogTitle>서비스 추가</DialogTitle>
          <DialogDescription>새로운 서비스를 추가합니다.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-1 flex-1 min-h-0 overflow-y-auto pr-1">
          {/* 폴더명 */}
          <div className="grid gap-2">
            <label htmlFor="add-folder" className="text-sm font-medium">
              서비스 이름(폴더명) <span className="text-red-500">*</span>
            </label>
            <Input
              id="add-folder"
              type="text"
              autoComplete="off"
              value={folder}
              onChange={handleFolderChange}
              placeholder="예: asset-topology"
            />
          </div>

          {/* 서비스명 */}
          <div className="grid gap-2">
            <label htmlFor="add-name" className="text-sm font-medium">
              서비스 명 <span className="text-red-500">*</span>
            </label>
            <Input
              id="add-name"
              value={name}
              onChange={handleNameChange}
              placeholder="예: 자산 Topology"
            />
          </div>

          {/* 설명 */}
          <div className="grid gap-2">
            <label htmlFor="add-description" className="text-sm font-medium">
              서비스 설명 <span className="text-red-500">*</span>
            </label>
            <Input
              id="add-description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="서비스 설명을 입력하세요"
            />
          </div>

          {/* 컴포넌트 선택 */}
          <div className="grid gap-2">
            <label className="text-sm font-medium">컴포넌트 선택 <span className="text-red-500">*</span></label>
            <div className="flex flex-col gap-3 p-4 border rounded-md">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="add-component-backend"
                  checked={components.backend}
                  onCheckedChange={(checked) => handleComponentChange('backend', !!checked)}
                />
                <label htmlFor="add-component-backend" className="text-sm font-medium leading-none cursor-pointer">
                  Backend
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="add-component-frontend"
                  checked={components.frontend}
                  onCheckedChange={(checked) => handleComponentChange('frontend', !!checked)}
                />
                <label htmlFor="add-component-frontend" className="text-sm font-medium leading-none cursor-pointer">
                  Frontend
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="add-component-frontend-admin"
                  checked={components['frontend-admin']}
                  onCheckedChange={(checked) => handleComponentChange('frontend-admin', !!checked)}
                />
                <label htmlFor="add-component-frontend-admin" className="text-sm font-medium leading-none cursor-pointer">
                  Frontend Admin
                </label>
              </div>
            </div>
          </div>

          {/* 포트 설정 */}
          <div className="grid gap-2 p-4 bg-muted rounded-md">
            <label className="text-sm font-medium">포트 설정</label>
            <p className="text-xs text-muted-foreground mb-2">
              Index를 선택하면 해당 index의 포트가 자동으로 계산되어 할당됩니다.
              <br />
              Frontend: 4000 + Index, Backend: 5000 + Index, Frontend Admin: 6000 + Index
            </p>
            <div className="grid gap-2 mb-4">
              <label htmlFor="add-port-index" className="text-sm font-medium">
                Port Index <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedIndex?.toString() || ''}
                onValueChange={(value) => handleIndexChange(parseInt(value))}
              >
                <SelectTrigger id="add-port-index" className="w-full">
                  <SelectValue placeholder="Select an index" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  sideOffset={4}
                  collisionPadding={16}
                  className="z-[200] max-h-72"
                >
                  {selectItems}
                </SelectContent>
              </Select>
              {selectedIndex && (
                <p className="text-xs text-muted-foreground">
                  Selected Index: {selectedIndex} → Frontend: {ports.frontend}, Backend: {ports.backend}, Admin: {ports['frontend-admin']}
                </p>
              )}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <label htmlFor="add-frontend-port" className="text-sm font-medium">
                  Frontend Port {components.frontend && <span className="text-red-500">*</span>}
                </label>
                <Input
                  id="add-frontend-port"
                  type="number"
                  value={components.frontend ? ports.frontend : ''}
                  disabled
                  min={4001}
                  max={4100}
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="add-backend-port" className="text-sm font-medium">
                  Backend Port {components.backend && <span className="text-red-500">*</span>}
                </label>
                <Input
                  id="add-backend-port"
                  type="number"
                  value={components.backend ? ports.backend : ''}
                  disabled
                  min={5001}
                  max={5100}
                />
              </div>
              <div className="grid gap-2">
                <label htmlFor="add-frontend-admin-port" className="text-sm font-medium">
                  Frontend Admin Port {components['frontend-admin'] && <span className="text-red-500">*</span>}
                </label>
                <Input
                  id="add-frontend-admin-port"
                  type="number"
                  value={components['frontend-admin'] ? ports['frontend-admin'] : ''}
                  disabled
                  min={6001}
                  max={6100}
                />
              </div>
            </div>
          </div>

          {/* 아이콘 선택 */}
          <div className="grid gap-2">
            <label className="text-sm font-medium">아이콘</label>
            <ServiceIconPicker value={icon} onChange={handleIconSelect} />
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            취소
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? '저장 중...' : '저장'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// React.memo로 메모이제이션 (성능 최적화)
export const AddServiceDialog = React.memo(AddServiceDialogComponent);

AddServiceDialog.displayName = 'AddServiceDialog';
